import React, { useReducer, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import CampaignForm from './CampaignForm'
import VariationIndicator from '../VariationIndicator'
import RunningCampaign from '../RunningCampaign/RunningCampaign'
import { isCampaignRunning, campaignKeywords, tcDisplayName } from '../../../../api/models'
import { useCampaignSetup } from '../Context/CampaignSetupContext'

function updateReducer(state, action) {
  return {
    ...state,
    totalHeadlines: action.payload.headline?.filter(Boolean).length ?? 0,
    totalBodies: action.payload.body?.filter(Boolean).length ?? 0,
    totalMediaFiles: action.payload.media?.filter(Boolean).length ?? 0,
  }
}

export function CampaignEditor({
  eventId,
  eventDate,
  eventInfo,
  campaign,
  goal,
  onCancel,
  showValidationErrors = false,
  ...rest
}) {
  const { t } = useTranslation()
  const { setup, canAnonEdit } = useCampaignSetup()
  const creative = useMemo(
    () => setup.creatives.find((x) => x.tc === campaign.tc) || { tc: campaign.tc },
    [setup.creatives, campaign.tc]
  )
  const initialCounts = {
    totalHeadlines: creative.headline?.filter(Boolean).length ?? 0,
    totalBodies: creative.body?.filter(Boolean).length ?? 0,
    totalMediaFiles: creative.media?.filter(Boolean).length ?? 0,
  }
  const [creativeCounts, dispatchCreativeCountsAction] = useReducer(updateReducer, initialCounts)
  useEffect(() => {
    // update counts
    dispatchCreativeCountsAction({ payload: creative })
  }, [creative, dispatchCreativeCountsAction])

  if (!canAnonEdit) {
    const targeting = (setup.targeting || []).filter(
      (x) => x.tc === campaign.tc && x.tc_run_id === campaign.tc_run_id,
    )

    return (
      <RunningCampaign
        eventId={String(eventId)}
        tc={campaign.tc}
        tc_run_id={campaign.tc_run_id}
        keywords={campaignKeywords(campaign).headline}
        creative={creative}
        targeting={targeting}
        integrationDetails={setup.integration_details}
        tasteCluster={t('Recommendations.campaign.recommendationTitle', {
          tc: tcDisplayName(campaign),
        })}
        audienceId={creative.audience_id}
        savedAudienceId={creative.saved_audience_id}
      />
    )
  }

  return (
    <>
      {!isCampaignRunning(campaign) && <VariationIndicator {...creativeCounts} />}
      <CampaignForm
        eventId={eventId}
        eventDate={eventDate}
        eventInfo={eventInfo}
        campaign={campaign}
        goal={goal}
        onCancel={onCancel}
        showValidationErrors={showValidationErrors}
        {...rest}
      />
    </>
  )
}

CampaignEditor.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  eventDate: PropTypes.string.isRequired,
  eventInfo: PropTypes.object,
  campaign: PropTypes.object.isRequired,
  goal: PropTypes.string,
  onCancel: PropTypes.func,
  showValidationErrors: PropTypes.bool,
}
